import { useLazyQuery } from '@apollo/client'
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  useToast
} from '@chaine/keychaine'
import React, { ReactNode, useState } from 'react'

import { MetricsDisplay } from '../../_shared/components'
import { CONNECTION_METRICS, WORKSPACE_METRICS } from '../../_shared/queries'
import { IPerformanceMetrics } from '../../_shared/types'
import { MetricsLoadingSkeleton } from './metrics-loading-skeleton'

const EMPTY_METRICS: IPerformanceMetrics = {
  onTimePerformance: {
    recentAverageMinutesDelayedForDelivery: null,
    recentAverageMinutesDelayedForPickup: null,
    recentOnTimeDeliveryPercentage: null,
    recentOnTimePercentageOverall: null,
    recentOnTimePickupPercentage: null,
    totalAverageMinutesDelayedForDelivery: null,
    totalAverageMinutesDelayedForPickup: null,
    totalAverageMinutesDelayedOverall: null,
    totalOnTimeDeliveryPercentage: null,
    totalOnTimePercentageOverall: null,
    totalOnTimePickupPercentage: null
  },
  trackingPerformance: {
    recentPercentageOfFakeLoadsTracked: null,
    recentTrackingCompliancePercentage: null,
    totalPercentageOfFakeLoadsTracked: null,
    totalTrackedIOTDevicePercentage: null,
    totalTrackedMobileAppPercentage: null,
    totalTrackedViaAPIPercentage: null,
    totalTrackedViaELDPercentage: null,
    totalTrackedViaExternalTrackingLinkPercentage: null,
    totalTrackedViaSFTPPercentage: null,
    totalTrackingCompliancePercentage: null
  }
}

interface IMetricsData {
  metrics: {
    performanceMetrics: IPerformanceMetrics
  }
  workspaceName: string
}

interface MetricsDrawerProps {
  connectionID?: string
  trigger: (props: { isDisabled?: boolean; onClick: () => void }) => ReactNode
  workspaceID?: string
  workspaceName: string
  workspaceOnly?: boolean
}

export const MetricsDrawer = ({
  workspaceName,
  workspaceID,
  connectionID,
  workspaceOnly = false,
  trigger
}: MetricsDrawerProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()

  const [selectedWorkspaceMetrics, setSelectedWorkspaceMetrics] = useState<IMetricsData | null>(null)
  const [selectedConnectionMetrics, setSelectedConnectionMetrics] = useState<IMetricsData | null>(null)

  const [fetchWorkspaceMetrics, { loading: loadingWorkspaceMetrics }] = useLazyQuery(WORKSPACE_METRICS, {
    onCompleted: (data) => {
      if (data?.workspaceMetrics) {
        setSelectedWorkspaceMetrics({
          metrics: { performanceMetrics: data.workspaceMetrics.performanceMetrics },
          workspaceName
        })
      }
    },
    onError: (error) =>
      toast({
        description: error.message,
        duration: 5000,
        isClosable: true,
        status: 'error',
        title: 'Error loading workspace metrics'
      })
  })

  const [fetchConnectionMetrics, { loading: loadingConnectionMetrics }] = useLazyQuery(CONNECTION_METRICS, {
    onCompleted: (data) => {
      if (data?.connectionMetrics) {
        setSelectedConnectionMetrics({
          metrics: { performanceMetrics: data.connectionMetrics.performanceMetrics },
          workspaceName
        })
      }
    },
    onError: (error) =>
      toast({
        description: error.message,
        duration: 5000,
        isClosable: true,
        status: 'error',
        title: 'Error loading connection metrics'
      })
  })

  const handleOpen = () => {
    onOpen()

    if (workspaceID) {
      fetchWorkspaceMetrics({ variables: { workspaceID } })
    }

    if (connectionID && !workspaceOnly) {
      fetchConnectionMetrics({ variables: { connectionID } })
    }
  }

  return (
    <>
      {trigger({ isDisabled: !workspaceID, onClick: handleOpen })}
      {isOpen && (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Performance metrics</DrawerHeader>
            <DrawerBody>
              <Heading size="lg" mb={5} fontWeight="semibold">
                {workspaceName}
              </Heading>

              {workspaceOnly ? (
                loadingWorkspaceMetrics ? (
                  <MetricsLoadingSkeleton />
                ) : (
                  <MetricsDisplay metrics={selectedWorkspaceMetrics?.metrics?.performanceMetrics ?? EMPTY_METRICS} />
                )
              ) : (
                <Tabs variant="enclosed" isLazy>
                  <TabList>
                    <Tab>Platform metrics</Tab>
                    {connectionID && <Tab>Company metrics</Tab>}
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      {loadingWorkspaceMetrics ? (
                        <MetricsLoadingSkeleton />
                      ) : (
                        <MetricsDisplay
                          metrics={selectedWorkspaceMetrics?.metrics?.performanceMetrics ?? EMPTY_METRICS}
                        />
                      )}
                    </TabPanel>
                    {connectionID && (
                      <TabPanel>
                        {loadingConnectionMetrics ? (
                          <MetricsLoadingSkeleton />
                        ) : (
                          <MetricsDisplay
                            metrics={selectedConnectionMetrics?.metrics?.performanceMetrics ?? EMPTY_METRICS}
                          />
                        )}
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  )
}
